/**
 * « Mes classes » — le rangement du professeur.
 *
 * Le fondateur, 14 septembre 2026 : « il faudra lui ajouter la possibilité de
 * creer une classe et d'y ajouter des élèves et générer leurs accès et les
 * monitorer », puis « le code de suivi d'abord, la classe ensuite ».
 *
 * ─── LA RÈGLE, RENDUE VISIBLE À L'ÉCRAN ────────────────────────────────────
 *
 * Une classe ORGANISE, elle ne donne pas accès. On n'y range que des élèves
 * DÉJÀ suivis : la liste déroulante ne propose donc que le périmètre réel du
 * professeur, lu du contexte (`useTuteur()`), et non une recherche ouverte sur
 * tous les élèves du produit. Un professeur ne peut pas « trouver » ici un
 * élève qu'il ne suit pas — il n'y a rien à deviner.
 *
 * Quand le périmètre est vide, l'écran ne propose pas un champ mort : il
 * renvoie vers « Mes élèves », où le code de suivi est le seul chemin d'entrée.
 *
 * ─── Retirer n'est pas rompre ──────────────────────────────────────────────
 *
 * « Retirer de la classe » ne touche ni au suivi, ni aux travaux, ni à
 * l'historique — et le dit. Le bouton est donc sobre (`.btn--danger` de la
 * console : fond de carte, texte étincelle, jamais de rouge plein), parce que
 * ranger autrement n'est pas un incident.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import type { NiveauScolaire } from '@silex/shared';

import { api, ApiError, type ClasseVue } from '../api/client';
import { Chargement, ErreurRessource } from '../components/EtatRessource';
import { adressesParcours } from '../lib/manuel-tuteur-format';
import { manuelVivantActif } from '../lib/manuel-vivant';
import { useAsyncRechargeable } from '../lib/useAsync';
import { libelleNiveauScolaire, pluriel } from '../lib/tuteur-format';
import { useTuteur } from '../tuteur/TuteurContext';

export function MaClassePage() {
  const { eleves } = useTuteur();
  const loader = useMemo(() => () => api.listerClasses(), []);
  const { resource, recharger } = useAsyncRechargeable(loader);

  const [nom, setNom] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const mesEleves = eleves.status === 'ready' ? eleves.data.eleves : [];

  async function creer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErreur(null);
    setEnvoi(true);
    try {
      await api.creerClasse(nom);
      setNom('');
      recharger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Erreur inattendue');
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <div className="content__inner">
      <div className="page-head">
        <div>
          <h1>Mes classes</h1>
          <p className="page-sub">
            Une classe range les élèves que vous suivez déjà. Elle ne donne aucun accès : celui-ci
            vient du code de suivi que l’élève vous confie, dans{' '}
            <Link to="/eleves">Mes élèves</Link>.
          </p>
        </div>
      </div>

      <form className="card" onSubmit={creer} noValidate>
        <h3>Créer une classe</h3>
        {erreur ? (
          <p className="callout callout--rare" role="alert">
            {erreur}
          </p>
        ) : null}
        <div className="filters">
          <label className="field field--inline">
            <span>Nom de la classe</span>
            <input
              className="control"
              value={nom}
              placeholder="3e B"
              onChange={(e) => setNom(e.target.value)}
            />
          </label>
          <button type="submit" className="btn btn--primary" disabled={envoi || nom.trim() === ''}>
            {envoi ? 'Création…' : 'Créer'}
          </button>
        </div>
      </form>

      {resource.status === 'loading' ? <Chargement quoi="vos classes" /> : null}
      {resource.status === 'error' ? <ErreurRessource message={resource.message} /> : null}

      {resource.status === 'ready' ? (
        resource.data.classes.length === 0 ? (
          <p className="card-sub">Aucune classe pour l’instant.</p>
        ) : (
          resource.data.classes.map((classe) => (
            <CarteClasse
              key={classe.id}
              classe={classe}
              mesEleves={mesEleves}
              onChange={recharger}
            />
          ))
        )
      ) : null}
    </div>
  );
}

interface EleveDuPerimetre {
  readonly eleveId: string;
  readonly prenom: string;
  readonly niveauScolaire: NiveauScolaire;
}

function CarteClasse({
  classe,
  mesEleves,
  onChange,
}: {
  classe: ClasseVue;
  mesEleves: readonly EleveDuPerimetre[];
  onChange: () => void;
}) {
  const [choix, setChoix] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  /* Seuls les élèves du périmètre QUI NE SONT PAS DÉJÀ dans cette classe. */
  const ajoutables = useMemo(() => {
    const dedans = new Set(classe.membres.map((m) => m.eleveId));
    return mesEleves.filter((e) => !dedans.has(e.eleveId));
  }, [classe.membres, mesEleves]);

  async function agir(action: () => Promise<unknown>) {
    setErreur(null);
    setEnCours(true);
    try {
      await action();
      onChange();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Erreur inattendue');
    } finally {
      setEnCours(false);
    }
  }

  return (
    <section className="card">
      <div className="row-between">
        <h3>{classe.nom}</h3>
        {/* Lot P3 (AB#975) — les parcours de la classe, derrière le drapeau du manuel. */}
        {manuelVivantActif() ? (
          <span className="row">
            <Link
              className="btn btn--ghost btn--sm"
              to={adressesParcours.suiviClasse(classe.id)}
              data-testid="classe-suivi-parcours"
            >
              Parcours
            </Link>
            {classe.nbMembres > 0 ? (
              <Link
                className="btn btn--ghost btn--sm"
                to={adressesParcours.composer({ classe: classe.id })}
                data-testid="classe-nouveau-parcours"
              >
                Nouveau parcours
              </Link>
            ) : null}
          </span>
        ) : null}
      </div>
      <p className="card-sub">
        {classe.nbMembres === 0
          ? 'Aucun élève dans cette classe.'
          : `${classe.nbMembres} ${pluriel(classe.nbMembres, 'élève', 'élèves')}.`}
      </p>

      {erreur ? (
        <p className="callout callout--rare" role="alert">
          {erreur}
        </p>
      ) : null}

      {classe.membres.map((membre) => (
        <div key={membre.eleveId} className="filters">
          <span>
            {membre.prenom} — {libelleNiveauScolaire(membre.niveauScolaire)}
          </span>
          <button
            type="button"
            className="btn btn--danger"
            disabled={enCours}
            onClick={() => void agir(() => api.retirerEleveDeClasse(classe.id, membre.eleveId))}
          >
            Retirer de la classe
          </button>
        </div>
      ))}

      {mesEleves.length === 0 ? (
        <p className="card-sub">
          Vous ne suivez encore aucun élève. Demandez son code de suivi à un élève depuis{' '}
          <Link to="/eleves">Mes élèves</Link>.
        </p>
      ) : ajoutables.length === 0 ? (
        <p className="card-sub">Tous les élèves que vous suivez sont déjà dans cette classe.</p>
      ) : (
        <div className="filters">
          <label className="field field--inline">
            <span>Ajouter un élève que vous suivez</span>
            <select className="control" value={choix} onChange={(e) => setChoix(e.target.value)}>
              <option value="">Choisir…</option>
              {ajoutables.map((e) => (
                <option key={e.eleveId} value={e.eleveId}>
                  {e.prenom} — {libelleNiveauScolaire(e.niveauScolaire)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn btn--primary"
            disabled={enCours || choix === ''}
            onClick={() =>
              void agir(async () => {
                await api.ajouterEleveAClasse(classe.id, choix);
                setChoix('');
              })
            }
          >
            Ajouter
          </button>
        </div>
      )}

      <p className="card-sub">
        Retirer un élève de la classe ne touche ni à son suivi, ni à ses travaux.
      </p>
    </section>
  );
}
